/* Wildtera — the product gallery.
 *
 * Every slide is already in the DOM (see snippets/wt-product-gallery.liquid);
 * this only decides which one is showing. That means the gallery works as a
 * plain vertical stack of images if this file never loads — the first slide is
 * visible, the rest are behind it, and nothing is broken, only static.
 *
 * Since 22 October 2026 every slide after the first holds its photograph in a
 * <template data-gallery-defer>, so the page fetches one photograph, not six.
 * hydrate() fills a slide in: the one being moved to and its neighbours on
 * every move, the one the lightbox opens on, and the rest once the page has
 * loaded and the browser is idle.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('wt-gallery')) return;

  var KEY = {
    LEFT: 'ArrowLeft',
    RIGHT: 'ArrowRight',
    HOME: 'Home',
    END: 'End',
  };

  var WtGallery = class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.counters = Array.from(this.querySelectorAll('[data-gallery-current]'));
      this.status = this.querySelector('[data-gallery-status]');
      this.stage = this.querySelector('.wt-gallery__stage');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = this.querySelector('[data-gallery-lightbox-image]');
      this.lightboxVideo = this.querySelector('[data-gallery-lightbox-video]');

      if (this.slides.length === 0) return;

      this.index = 0;
      this.total = this.slides.length;

      this.onKeydown = this.onKeydown.bind(this);
      this.bind();
      this.select(0, { silent: true });
      this.hydrateLater();
    }

    hydrate(index) {
      var slide = this.slides[index];
      if (!slide) return;
      var tpl = slide.querySelector('template[data-gallery-defer]');
      if (!tpl) return;
      var content = tpl.content.cloneNode(true);
      /* Asked for now, so fetched now: a lazy image in a hidden slide would
         wait for nothing. */
      content.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
      tpl.replaceWith(content);
    }

    hydrateLater() {
      var self = this;
      var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
      var rest = function () {
        idle(function () {
          for (var i = 1; i < self.total; i++) self.hydrate(i);
        });
      };
      if (document.readyState === 'complete') rest();
      else window.addEventListener('load', rest, { once: true });
    }

    bind() {
      var self = this;

      this.thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          self.select(i);
        });
      });

      this.querySelectorAll('[data-gallery-prev]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.step(-1);
        });
      });

      this.querySelectorAll('[data-gallery-next]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.step(1);
        });
      });

      var expand = this.querySelector('[data-gallery-expand]');
      if (expand) {
        expand.addEventListener('click', function () {
          self.open();
        });
      }

      var close = this.querySelector('[data-gallery-close]');
      if (close) {
        close.addEventListener('click', function () {
          self.close();
        });
      }

      if (this.lightbox) {
        /* Clicking the backdrop closes. The dialog element itself fills the
           viewport, so a click that lands on it rather than on its inner panel
           is a backdrop click. */
        this.lightbox.addEventListener('click', function (event) {
          if (event.target === self.lightbox) self.close();
        });
        this.lightbox.addEventListener('close', function () {
          document.documentElement.style.removeProperty('overflow');
          self.stopClip();
          /* The slide's own loop was held while the dialog covered it. */
          document.dispatchEvent(new CustomEvent('wildtera:loops', { detail: { pause: false } }));
        });
      }

      /* Arrow keys work anywhere inside the gallery — on a thumbnail, on the
         stage, or in the open lightbox. */
      this.addEventListener('keydown', this.onKeydown);

      this.bindSwipe();
    }

    onKeydown(event) {
      if (event.key === KEY.LEFT) {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === KEY.RIGHT) {
        event.preventDefault();
        this.step(1);
      } else if (event.key === KEY.HOME) {
        event.preventDefault();
        this.select(0);
      } else if (event.key === KEY.END) {
        event.preventDefault();
        this.select(this.total - 1);
      }
    }

    /* Horizontal swipe on the stage. The 40px threshold and the 1.4x
       horizontal-over-vertical test together stop a thumb that is scrolling the
       page from also flicking the gallery. */
    bindSwipe() {
      if (!this.stage) return;

      var self = this;
      var startX = 0;
      var startY = 0;
      var tracking = false;

      this.stage.addEventListener(
        'touchstart',
        function (event) {
          if (event.touches.length !== 1) return;
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
          tracking = true;
        },
        { passive: true }
      );

      this.stage.addEventListener(
        'touchend',
        function (event) {
          if (!tracking) return;
          tracking = false;

          var touch = event.changedTouches[0];
          var dx = touch.clientX - startX;
          var dy = touch.clientY - startY;

          if (Math.abs(dx) < 40) return;
          if (Math.abs(dx) < Math.abs(dy) * 1.4) return;

          self.step(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    step(delta) {
      var next = (this.index + delta + this.total) % this.total;
      this.select(next);
    }

    select(index, options) {
      var silent = options && options.silent;
      this.index = index;
      this.hydrate(index);
      /* The neighbours too, on a person's move, so the next arrow press finds
         its photograph ready; not on the silent first select at load. */
      if (!silent) {
        this.hydrate((index + 1) % this.total);
        this.hydrate((index - 1 + this.total) % this.total);
      }

      /* A control on the slide being left (the loop's Pause chip) would take
         keyboard focus down to <body> with it as the slide hides, and the
         arrow keys, which listen on the gallery, would stop. Hand focus to the
         new slide's thumbnail instead. */
      var focused = document.activeElement;
      var focusWasOnLeavingSlide = this.slides.some(function (slide, i) {
        return i !== index && slide.contains(focused);
      });

      this.slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.thumbs.forEach(function (thumb, i) {
        var active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      this.counters.forEach(function (counter) {
        counter.textContent = String(index + 1);
      });

      if (this.lightbox && this.lightbox.open) this.paintLightbox();

      /* A moving photograph on a slide plays only while that slide is the one
         showing (snippets/wt-video.liquid listens). Every slide sits in the
         same box, so the loop's own observers cannot tell. */
      document.dispatchEvent(new CustomEvent('wildtera:gallery', { detail: { index: index } }));

      if (focusWasOnLeavingSlide && this.thumbs[index]) {
        this.thumbs[index].focus({ preventScroll: true });
      }

      /* Announce the change only when a person caused it, so the live region
         does not fire on load. */
      if (!silent && this.status) {
        this.status.textContent = String(index + 1) + ' of ' + String(this.total);
      }

      this.scrollThumbIntoView(index);
    }

    scrollThumbIntoView(index) {
      var thumb = this.thumbs[index];
      if (!thumb || !thumb.parentElement) return;

      var rail = thumb.closest('[data-gallery-thumbs]');
      if (!rail) return;
      /* Only when the rail actually scrolls — on desktop it is a static column
         and scrollIntoView would drag the whole page. */
      if (rail.scrollWidth <= rail.clientWidth) return;

      var railBox = rail.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      var offset = thumbBox.left - railBox.left - (railBox.width - thumbBox.width) / 2;

      rail.scrollBy({ left: offset, behavior: 'smooth' });
    }

    paintLightbox() {
      if (!this.lightboxImage) return;

      var slide = this.slides[this.index];
      var image = slide && slide.querySelector('img');

      /* A slide carrying a moving photograph opens as the clip, not as its
         poster (14 October 2026: "None of the videos are playing on Full
         Screen"). Every other slide is the photograph, as before. */
      var clip = this.clipFor(slide);
      if (clip && this.lightboxVideo) {
        this.lightboxImage.hidden = true;
        this.showClip(clip, image);
        return;
      }
      this.stopClip();
      this.lightboxImage.hidden = false;
      if (!image) return;

      /* currentSrc is what the browser actually chose from the srcset, so the
         full-screen view reuses a file that is already in cache rather than
         starting a fresh download. */
      this.lightboxImage.src = image.currentSrc || image.src;
      this.lightboxImage.alt = image.alt || '';
      /* Overwrite the placeholder intrinsics from the markup with the real
         ones, so the dialog reserves the right box before the file decodes. */
      if (image.naturalWidth) {
        this.lightboxImage.width = image.naturalWidth;
        this.lightboxImage.height = image.naturalHeight;
      }
    }

    /* The largest rendition no taller than the screen can show, capped at
       1080, or the smallest there is. The sources are the ones the slide's
       wt-video-loop was given (snippets/wt-video.liquid), so the two can
       never point at different files. */
    clipFor(slide) {
      var loop = slide && slide.querySelector('wt-video-loop[data-sources]');
      if (!loop) return null;
      var sources;
      try {
        sources = JSON.parse(loop.dataset.sources || '[]').filter(function (s) {
          return s && s.u;
        });
      } catch (e) {
        return null;
      }
      if (!sources.length) return null;
      sources.sort(function (a, b) {
        return a.h - b.h;
      });
      var target = Math.min(1080, Math.ceil(window.innerHeight * (window.devicePixelRatio || 1)));
      var fits = sources.filter(function (s) {
        return s.h <= target;
      });
      return (fits.length ? fits[fits.length - 1] : sources[0]).u;
    }

    showClip(url, poster) {
      var video = this.lightboxVideo;
      if (poster) video.poster = poster.currentSrc || poster.src;
      if (video.getAttribute('src') !== url) {
        video.src = url;
      }
      video.hidden = false;
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (still) return;
      var playing = video.play();
      /* Refused (an autoplay policy, Low Power Mode): the controls are there. */
      if (playing && playing.catch) playing.catch(function () {});
    }

    stopClip() {
      var video = this.lightboxVideo;
      if (!video || !video.getAttribute('src')) return;
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.hidden = true;
    }

    open() {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;
      /* Hold the gallery's own loop: it would keep playing under the dialog. */
      document.dispatchEvent(new CustomEvent('wildtera:loops', { detail: { pause: true } }));
      this.hydrate(this.index);
      this.paintLightbox();
      document.documentElement.style.overflow = 'hidden';
      this.lightbox.showModal();
    }

    close() {
      if (!this.lightbox || !this.lightbox.open) return;
      this.lightbox.close();
    }
  };

  customElements.define('wt-gallery', WtGallery);
})();
